import React, { useCallback, useMemo, memo } from "react";
import { useFormikContext } from "formik";
import { Box, Grid, Paper, Checkbox, Alert } from "@mui/material";

import RRDDirectMailBulkSelection from "../bulk-selection/RRDDirectMailBulkSelection";
import useSearchFilter from "@/helpers/hooks/useSearchFilter";
import RpVirtualList from "@/components/virtual/RpVirtualList";
import RRDCampaignSingleLocation from "./RRDCampaignSingleLocation";
import MuiSearchInput from "@/components/muiCustom/search/MuiSearchInput";
import useLocationBulkSelection from "@/helpers/hooks/useLocationBulkSelection";
import BulkLocationCheckbox from "@/features/campaigns/components/bulkSelection/LocationCheckbox";
import { rrdDirectMailCampaignHelper } from "@/features/campaigns/direct-mail/rrd/utils/rrdDirectMailHelper";

const DirectMailBulkSelection = RRDDirectMailBulkSelection as React.ElementType;
const GridItem = Grid as React.ElementType;

type RRDLocation = Record<string, any>;
type RRDEligibleLocationsProps = {
  locations?: RRDLocation[];
  campaign?: Record<string, any>;
  expanded: any[];
  setExpanded: React.Dispatch<React.SetStateAction<any[]>>;
};

type LocationRowProps = {
  loc: RRDLocation;
  showLocationCheckBox?: boolean;
  isChecked?: boolean;
  setCheckedLocations: React.Dispatch<React.SetStateAction<any[]>>;
  isDisabled?: boolean;
  shouldExpandIt?: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<any[]>>;
  campaign?: Record<string, any>;
  couponExpirationSetBy?: string;
  fixedCouponDurationDays?: number | null;
};

const LocationRow = memo(
  ({
    loc,
    showLocationCheckBox,
    isChecked,
    setCheckedLocations,
    isDisabled,
    shouldExpandIt,
    setExpanded,
    campaign,
    couponExpirationSetBy,
    fixedCouponDurationDays,
  }: LocationRowProps) => {
    return (
      <Box sx={{ display: "flex", alignItems: "start", gap: 1 }}>
        {showLocationCheckBox && (
          <BulkLocationCheckbox
            locationId={loc.id}
            isChecked={isChecked}
            setCheckedLocations={setCheckedLocations}
            disabled={isDisabled}
          />
        )}
        <RRDCampaignSingleLocation
          loc={loc}
          shouldExpandIt={shouldExpandIt}
          setExpanded={setExpanded}
          campaign={campaign}
          storeCopyCPMAdmin={campaign?.storeCopyCpm}
          minCouponDurationAdmin={campaign?.adminMinimumCouponDurationDays}
          lastCouponExpiryDateAdmin={campaign?.adminCouponExpirationDate}
          couponExpirationSetBy={couponExpirationSetBy}
          fixedCouponDurationDays={fixedCouponDurationDays}
          distributionCostAdmin={loc?.distribution?.cost}
        />
      </Box>
    );
  }
);

function RRDEligibleLocations({
  locations = [],
  campaign,
  expanded,
  setExpanded,
}: RRDEligibleLocationsProps) {
  const couponExpirationSetBy = useMemo(
    () => rrdDirectMailCampaignHelper.getCouponExpirationSetBy(campaign),
    [campaign]
  );
  const fixedCouponDurationDays = useMemo(
    () => rrdDirectMailCampaignHelper.getFixedCouponDurationDays(campaign),
    [campaign]
  );
  const {
    showLocationCheckBox,
    checkedLocations,
    disableLocations,
    isAllSelected,
    setCheckedLocations,
    setDisableLocations,
    handleToggleBulkSelection,
    handleToggleGlobalSelection,
  } = useLocationBulkSelection(locations, "id");

  const { setValues, setFieldValue } = useFormikContext();
  const [searchKey, handleSearchChange, filteredSubscribedLocations] =
    useSearchFilter(locations, ["location_name"]);

  const marketList = useMemo(() => {
    const m = locations.map((l: RRDLocation) => ({
      print_type: l.print_type,
      market: l.market_name,
    }));

    return [...new Set(m.map((item) => JSON.stringify(item)))].map((item) =>
      JSON.parse(item)
    );
  }, [locations]);

  const handleOnToggleBulkSelection = useCallback(
    (isOpened: boolean) => {
      handleToggleBulkSelection(isOpened);
      if (isOpened) {
        setExpanded([]);
      }
    },
    [setExpanded, handleToggleBulkSelection]
  );

  const renderItem = useCallback(
    (loc: RRDLocation) => {
      if (!loc) return null;
      return (
        <LocationRow
          loc={loc}
          showLocationCheckBox={showLocationCheckBox}
          isChecked={checkedLocations.includes(loc.id)}
          setCheckedLocations={setCheckedLocations}
          isDisabled={disableLocations.includes(loc.id)}
          shouldExpandIt={expanded.includes(loc.id)}
          setExpanded={setExpanded}
          campaign={campaign}
          couponExpirationSetBy={couponExpirationSetBy}
          fixedCouponDurationDays={fixedCouponDurationDays}
        />
      );
    },
    [
      showLocationCheckBox,
      checkedLocations,
      setCheckedLocations,
      disableLocations,
      expanded,
      setExpanded,
      campaign,
      couponExpirationSetBy,
      fixedCouponDurationDays,
    ]
  );

  return (
    <div>
      {locations?.length > 1 ? (
        <DirectMailBulkSelection
          onToggleBulkSelection={handleOnToggleBulkSelection}
          campaignData={campaign}
          checkedLocations={checkedLocations}
          setLocationValues={setValues}
          setLocationFieldValue={setFieldValue}
          locations={locations}
          setDisableLocations={setDisableLocations}
          disableLocations={disableLocations}
          minCouponDurationAdmin={campaign?.adminMinimumCouponDurationDays}
          lastCouponExpiryDateAdmin={campaign?.adminCouponExpirationDate}
          couponExpirationSetBy={couponExpirationSetBy}
          fixedCouponDurationDays={fixedCouponDurationDays}
          marketList={marketList}
          storeCopyCpm={campaign?.storeCopyCpm}
        />
      ) : null}

      <Grid container spacing={2} alignItems="center" sx={{ my: 2 }}>
        {showLocationCheckBox && (
          <GridItem item>
            <Paper>
              <Checkbox
                color="primary"
                checked={isAllSelected}
                onChange={(e) => handleToggleGlobalSelection(e.target.checked)}
                className="m-0 p-1"
              />
            </Paper>
          </GridItem>
        )}

        <Grid size={{ xs: 10, md: 6 }}>
          <MuiSearchInput value={searchKey} onChange={handleSearchChange} />
        </Grid>
      </Grid>

      {filteredSubscribedLocations &&
      filteredSubscribedLocations?.length === 0 ? (
        <Box sx={{ my: 5, textAlign: "center" }}>
          <Alert severity="info">
            <strong>No location found.</strong>
          </Alert>
        </Box>
      ) : (
        <RpVirtualList
          items={filteredSubscribedLocations}
          renderItem={renderItem}
        />
      )}
    </div>
  );
}

export default RRDEligibleLocations;
